import { STEP_IDS } from './steps';

export const FLOW_SNAPSHOT_VERSION = 1;

const VALID_STEP_IDS = new Set(Object.values(STEP_IDS));

export const createEmptyAnswers = () => ({
  intent: '',
  categories: [],
  categoryOther: '',
  role: '',
  skills: '',
  capacity: '',
  type: '',
  typeOther: '',
  duration: '',
  experienceLevels: [],
  likelihood: '',
  workLocation: '',
  locationCountry: '',
  city: '',
  openToTravel: '',
  title: '',
  hasDescription: '',
  description: '',
  deadline: '',
  examplesText: '',
  budgetType: '',
  budgetRange: '',
  budgetAmount: '',
  companyName: '',
  about: '',
  aboutCountry: '',
  aboutCity: '',
  email: '',
});

export const createEmptySnapshot = () => ({
  version: FLOW_SNAPSHOT_VERSION,
  answers: createEmptyAnswers(),
  params: {},
  /** Answer keys filled from the URL (TP-299); drives Category/Role skip. */
  prefilledKeys: [],
  currentStepId: STEP_IDS.INTENT,
  visitedStepIds: [STEP_IDS.INTENT],
});

const isValidStepId = (stepId) =>
  typeof stepId === 'string' && VALID_STEP_IDS.has(stepId);

const isStringArray = (value) =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isValidAnswers = (rawAnswers, emptyAnswers) => {
  if (
    !rawAnswers ||
    typeof rawAnswers !== 'object' ||
    Array.isArray(rawAnswers)
  ) {
    return false;
  }

  return Object.keys(rawAnswers).every((key) => {
    if (!(key in emptyAnswers)) {
      return true;
    }

    const value = rawAnswers[key];
    if (key === 'categories' || key === 'experienceLevels') {
      return isStringArray(value);
    }

    // Coerce later in normalizeSnapshot; reject only hard-invalid shapes here.
    if (value == null) {
      return true;
    }

    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  });
};

/**
 * Accept only plain objects whose values are strings.
 * Rejects arrays, nested objects, and non-string primitives.
 */
export const normalizeParams = (rawParams) => {
  if (!rawParams || typeof rawParams !== 'object' || Array.isArray(rawParams)) {
    return {};
  }

  const params = {};
  Object.keys(rawParams).forEach((key) => {
    const value = rawParams[key];
    if (typeof value === 'string') {
      params[key] = value;
    }
  });
  return params;
};

export const normalizeSnapshot = (raw) => {
  const empty = createEmptySnapshot();

  if (!raw || typeof raw !== 'object') {
    return empty;
  }

  if (raw.version !== undefined && raw.version !== FLOW_SNAPSHOT_VERSION) {
    return empty;
  }

  if (raw.currentStepId !== undefined && !isValidStepId(raw.currentStepId)) {
    return empty;
  }

  if (
    raw.visitedStepIds !== undefined &&
    (!Array.isArray(raw.visitedStepIds) ||
      raw.visitedStepIds.length === 0 ||
      raw.visitedStepIds.some((stepId) => !isValidStepId(stepId)))
  ) {
    return empty;
  }

  if (
    raw.prefilledKeys !== undefined &&
    (!Array.isArray(raw.prefilledKeys) ||
      raw.prefilledKeys.some((key) => typeof key !== 'string'))
  ) {
    return empty;
  }

  const rawAnswers =
    raw.answers &&
    typeof raw.answers === 'object' &&
    !Array.isArray(raw.answers)
      ? raw.answers
      : {};

  if (!isValidAnswers(rawAnswers, empty.answers)) {
    return empty;
  }

  const params = normalizeParams(raw.params);

  const visitedStepIds = Array.isArray(raw.visitedStepIds)
    ? raw.visitedStepIds.filter(Boolean)
    : empty.visitedStepIds;

  const prefilledKeys = Array.isArray(raw.prefilledKeys)
    ? raw.prefilledKeys.filter(
        (key) => typeof key === 'string' && key in empty.answers
      )
    : empty.prefilledKeys;

  const answers = {
    ...empty.answers,
  };
  Object.keys(empty.answers).forEach((key) => {
    if (!(key in rawAnswers)) {
      return;
    }
    const value = rawAnswers[key];
    if (key === 'categories' || key === 'experienceLevels') {
      answers[key] = isStringArray(value) ? value : empty.answers[key];
      return;
    }
    if (value == null) {
      answers[key] = '';
      return;
    }
    answers[key] = typeof value === 'string' ? value : String(value);
  });

  return {
    version: FLOW_SNAPSHOT_VERSION,
    answers,
    params,
    prefilledKeys,
    currentStepId: isValidStepId(raw.currentStepId)
      ? raw.currentStepId
      : empty.currentStepId,
    visitedStepIds:
      visitedStepIds.length > 0 ? visitedStepIds : empty.visitedStepIds,
  };
};

/**
 * True when any answer field has a non-empty value (typed or URL prefill).
 * @param {Object} answers
 * @returns {boolean}
 */
export const hasAnswerProgress = (answers = {}) => {
  if (!answers || typeof answers !== 'object') {
    return false;
  }

  const emptyAnswers = createEmptyAnswers();
  return Object.keys(emptyAnswers).some((key) => {
    const value = answers[key];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return String(value == null ? '' : value).trim() !== '';
  });
};

/**
 * True when the user has progressed past the first step and can resume.
 * Requires real answer content so a bare mid-flow URL (or post-success
 * browser Back) does not look like a resumable draft.
 */
export const hasResumableProgress = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return false;
  }

  if (!hasAnswerProgress(snapshot.answers)) {
    return false;
  }

  const visitedStepIds = Array.isArray(snapshot.visitedStepIds)
    ? snapshot.visitedStepIds.filter(Boolean)
    : [];

  if (visitedStepIds.length > 1) {
    return true;
  }

  return Boolean(
    snapshot.currentStepId && snapshot.currentStepId !== STEP_IDS.INTENT
  );
};

/**
 * True when answers are complete enough to create a job on /post/pending.
 * Blocks empty/demo posts from opening pending with no flow data.
 * @param {Object} answers
 * @returns {boolean}
 */
export const isReadyForPendingSubmission = (answers = {}) => {
  if (!answers || typeof answers !== 'object') {
    return false;
  }

  if (!String(answers.intent || '').trim()) {
    return false;
  }

  return Boolean(
    String(answers.title || '').trim() ||
      String(answers.description || '').trim() ||
      String(answers.role || '').trim() ||
      String(answers.skills || '').trim() ||
      String(answers.capacity || '').trim()
  );
};
